import React from 'react';
import { Box, Checkbox, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import { ListChildComponentProps } from 'react-window';
import { Teacher } from '../../../../interfaces/examiner/Teacher';
import { Exam } from '../../../../interfaces/examiner/Exam';
import { SchoolGroup } from './types';
import { Classes } from './styles';
import { PRIMARY_TYPO, SECONDARY_TYPO } from './helpers';

export interface ExamRowData {
  items: Exam[];
  selectedIds: Set<string>;
  classes: Classes;
  onToggle: (exam: Exam) => void;
}

export const ExamRow = React.memo(function ExamRow({
  index,
  style,
  data,
}: ListChildComponentProps<ExamRowData>) {
  const { items, selectedIds, classes, onToggle } = data;
  const exam = items[index];
  const selected = selectedIds.has(exam.id);
  const teacherCount = exam.teachers?.length ?? 0;
  const initials = exam.title.slice(0, 2).toUpperCase();
  return (
    <ListItem
      style={{ ...style, boxSizing: 'border-box' }}
      button
      dense
      disableRipple
      className={`${classes.listItem} ${selected ? classes.selectedItem : ''}`}
      onClick={() => onToggle(exam)}
    >
      <ListItemIcon style={{ minWidth: 32 }}>
        <Checkbox size="small" checked={selected} color="primary" style={{ padding: 0 }} />
      </ListItemIcon>
      <Box className={`${classes.avatar} ${classes.examAvatar}`}>{initials}</Box>
      <ListItemText
        className={classes.listItemText}
        primary={exam.title}
        secondary={`${teacherCount} teacher${teacherCount !== 1 ? 's' : ''}`}
        primaryTypographyProps={PRIMARY_TYPO}
        secondaryTypographyProps={SECONDARY_TYPO}
      />
    </ListItem>
  );
});

export interface SchoolRowData {
  items: SchoolGroup[];
  selectedIds: Set<string>;
  classes: Classes;
  onToggle: (school: SchoolGroup) => void;
}

export const SchoolRow = React.memo(function SchoolRow({
  index,
  style,
  data,
}: ListChildComponentProps<SchoolRowData>) {
  const { items, selectedIds, classes, onToggle } = data;
  const school = items[index];
  const selected = selectedIds.has(school.name);
  const initials = school.name.slice(0, 2).toUpperCase();
  return (
    <ListItem
      style={{ ...style, boxSizing: 'border-box' }}
      button
      dense
      disableRipple
      className={`${classes.listItem} ${selected ? classes.selectedItem : ''}`}
      onClick={() => onToggle(school)}
    >
      <ListItemIcon style={{ minWidth: 32 }}>
        <Checkbox size="small" checked={selected} color="primary" style={{ padding: 0 }} />
      </ListItemIcon>
      <Box className={`${classes.avatar} ${classes.schoolAvatar}`}>{initials}</Box>
      <ListItemText
        className={classes.listItemText}
        primary={school.name}
        secondary={`${school.teacherCount} teacher${school.teacherCount !== 1 ? 's' : ''}`}
        primaryTypographyProps={PRIMARY_TYPO}
        secondaryTypographyProps={SECONDARY_TYPO}
      />
    </ListItem>
  );
});

export interface TeacherRowData {
  items: Teacher[];
  selectedIds: Set<string>;
  classes: Classes;
  onToggle: (teacher: Teacher) => void;
}

export const TeacherRow = React.memo(function TeacherRow({
  index,
  style,
  data,
}: ListChildComponentProps<TeacherRowData>) {
  const { items, selectedIds, classes, onToggle } = data;
  const teacher = items[index];
  const selected = selectedIds.has(teacher.id);
  const initials = (teacher.name || '?').slice(0, 2).toUpperCase();
  return (
    <ListItem
      style={{ ...style, boxSizing: 'border-box' }}
      button
      dense
      disableRipple
      className={`${classes.listItem} ${selected ? classes.selectedItem : ''}`}
      onClick={() => onToggle(teacher)}
    >
      <ListItemIcon style={{ minWidth: 32 }}>
        <Checkbox size="small" checked={selected} color="primary" style={{ padding: 0 }} />
      </ListItemIcon>
      <Box className={`${classes.avatar} ${classes.teacherAvatar}`}>{initials}</Box>
      <ListItemText
        className={classes.listItemText}
        primary={teacher.name}
        secondary={teacher.school}
        primaryTypographyProps={PRIMARY_TYPO}
        secondaryTypographyProps={SECONDARY_TYPO}
      />
    </ListItem>
  );
});
